import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface TeamMember {
  id: string;
  username: string;
  email: string;
  firstName: string;
  roleName: string;
  roleId: string;
  invitationStatus: string;
  joinedAt: string;
}

/** Legacy invitation shape (team_invitations collection) */
export interface LegacyInvitation {
  _id: string;
  inviteCode: string;
  roleId: string;
  maxUses: number | null;
  usedCount: number;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
}

/** Modern invitation shape (workspace_invitations collection) */
export interface WorkspaceInvitation {
  _id: string;
  email?: string;
  role: string;
  type: "email" | "link" | "direct";
  status: "pending" | "accepted" | "declined" | "expired" | "revoked";
  token: string;
  maxUses: number | null;
  usedCount: number;
  expiresAt?: string;
  invitedBy?: { userName?: string; email?: string };
  createdAt: string;
}

export interface Role {
  _id: string;
  name: string;
  description: string;
  permissions: Record<string, boolean>;
  isDefault: boolean;
}

// ─── Team members ─────────────────────────────────────────────────────────────

export function useTeamMembers(accountId?: string) {
  return useQuery<TeamMember[]>({
    queryKey: ["/api/team", accountId, "members"],
    queryFn: async () => {
      return await apiRequest("GET", `/api/team/${accountId}/members`) as TeamMember[];
    },
    enabled: !!accountId,
  });
}

export function useUpdateMember() {
  return useApiMutation({
    endpoint: (vars: { accountId: string; memberId: string }) =>
      `/api/team/${vars.accountId}/members/${vars.memberId}`,
    method: "PATCH",
    // invalidate takes an ARRAY whose elements may be key-returning functions,
    // each returning ONE query key. A bare function was passed here, so
    // .forEach threw inside onSuccess and the members list never refreshed
    // after an update (fixed Aug 2026).
    invalidate: [(vars: { accountId: string }) => ["/api/team", vars.accountId, "members"]],
  });
}

export function useRemoveMember() {
  return useApiMutation({
    endpoint: (vars: { accountId: string; memberId: string }) =>
      `/api/team/${vars.accountId}/members/${vars.memberId}`,
    method: "DELETE",
    // Same array-of-key-functions contract as useUpdateMember (fixed Aug 2026).
    invalidate: [(vars: { accountId: string }) => ["/api/team", vars.accountId, "members"]],
  });
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export function useRoles(accountId?: string) {
  return useQuery<Role[]>({
    queryKey: ["/api/team", accountId, "roles"],
    queryFn: async () => {
      return await apiRequest("GET", `/api/team/${accountId}/roles`) as Role[];
    },
    enabled: !!accountId,
  });
}

// ─── Modern workspace-scoped invitations (workspace_invitations collection) ───
// These are the canonical hooks — use these in all new UI.

export function useWorkspaceInvitations(workspaceId?: string) {
  return useQuery<WorkspaceInvitation[]>({
    queryKey: ["/api/workspaces", workspaceId, "invitations"],
    queryFn: async () => {
      const data = await apiRequest("GET", `/api/workspaces/${workspaceId}/invitations`) as any;
      // API wraps the array in { invitations: [] }
      return (data.invitations ?? data) as WorkspaceInvitation[];
    },
    enabled: !!workspaceId,
  });
}

/** Send a single email invitation */
export function useCreateWorkspaceInvitation(workspaceId: string) {
  return useApiMutation({
    endpoint: `/api/workspaces/${workspaceId}/invitations`,
    method: "POST",
    invalidate: [["/api/workspaces", workspaceId, "invitations"]],
  });
}

/** Create a reusable link invitation */
export function useCreateLinkInvitation(workspaceId: string) {
  return useApiMutation({
    endpoint: `/api/workspaces/${workspaceId}/invitations/link`,
    method: "POST",
    invalidate: [["/api/workspaces", workspaceId, "invitations"]],
  });
}

/** Soft-revoke an invitation (sets status → revoked, preserves audit record) */
export function useRevokeInvitation(workspaceId: string) {
  return useApiMutation({
    endpoint: (vars: { id: string }) =>
      `/api/workspaces/${workspaceId}/invitations/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/workspaces", workspaceId, "invitations"]],
  });
}

/** Send batch email invitations */
export function useBatchInvite() {
  return useApiMutation({
    endpoint: (vars: { workspaceId: string; emails?: string[]; role?: string }) =>
      `/api/workspaces/${vars.workspaceId}/invitations/batch`,
    method: "POST",
    // Same array-of-key-functions contract as useUpdateMember (fixed Aug 2026).
    invalidate: [(vars: { workspaceId: string }) => ["/api/workspaces", vars.workspaceId, "invitations"]],
  });
}

// ─── Legacy team invitation hooks (team_invitations collection) ───────────────
// Kept for backward-compat with existing InvitationsTab link-invite UI.
// TODO: migrate InvitationsTab to workspace-scoped hooks and remove these.

/** @deprecated Use useWorkspaceInvitations instead */
export function useInvitations() {
  return useQuery<LegacyInvitation[]>({
    queryKey: ["/api/team/invitations"],
  });
}

/** @deprecated Use useCreateWorkspaceInvitation instead */
export function useCreateInvitation() {
  return useApiMutation({
    endpoint: "/api/team/invitations",
    method: "POST",
    invalidate: [["/api/team/invitations"]],
  });
}

/** @deprecated Use useRevokeInvitation instead */
export function useUpdateInvitation() {
  return useApiMutation({
    endpoint: (vars: { id: string }) => `/api/team/invitations/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/team/invitations"]],
  });
}

/** @deprecated Use useRevokeInvitation instead */
export function useDeleteInvitation() {
  return useApiMutation({
    endpoint: (vars: { id: string }) => `/api/team/invitations/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/team/invitations"]],
  });
}

/** @deprecated Use useCreateWorkspaceInvitation instead */
export function useSendEmailInvitation() {
  return useApiMutation({
    endpoint: "/api/team/invitations/send-email",
    method: "POST",
    invalidate: [["/api/team/invitations"]],
  });
}
